import { TOGGLE_LINK_COMMAND } from "@lexical/link";
import {
  $isListNode,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListNode,
  REMOVE_LIST_COMMAND,
} from "@lexical/list";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { INSERT_HORIZONTAL_RULE_COMMAND } from "@lexical/react/LexicalHorizontalRuleNode";
import { $createQuoteNode, $isHeadingNode } from "@lexical/rich-text";
import { $setBlocksType } from "@lexical/selection";
import { $findMatchingParent, $getNearestNodeOfType, mergeRegister } from "@lexical/utils";
import { sanitizeUrl } from "components/editor/utils/url";
import classNames from "components/ui/classNames";
import type { LexicalEditor, TextFormatType } from "lexical";
import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
  $isRootOrShadowRoot,
  COMMAND_PRIORITY_CRITICAL,
  FORMAT_TEXT_COMMAND,
  SELECTION_CHANGE_COMMAND,
} from "lexical";
import {
  AlignJustifyIcon,
  BoldIcon,
  ItalicIcon,
  Link2Icon,
  ListIcon,
  ListOrderedIcon,
  MinusIcon,
  QuoteIcon,
  StrikethroughIcon,
  UnderlineIcon,
} from "lucide-react";
import type { ReactNode } from "react";
import { useCallback, useEffect, useState } from "react";

const blockTypeToBlockName = {
  paragraph: "Normal",
  number: "Numbered List",
  bullet: "Bulleted List",
  quote: "Quote",
  check: "Check List",
  link: "Link",
};

export default function ToolbarPlugin(): JSX.Element {
  const [editor] = useLexicalComposerContext();
  const [activeEditor, setActiveEditor] = useState(editor);
  const [blockType, setBlockType] = useState<keyof typeof blockTypeToBlockName>("paragraph");
  // const [rootType, setRootType] = useState<keyof typeof rootTypeToRootName>("root");
  // const [selectedElementKey, setSelectedElementKey] = useState<NodeKey | null>(null);
  // const [isLink, setIsLink] = useState(false);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [isEditable, setIsEditable] = useState(() => editor.isEditable());
  const [isLink, setIsLinkEditMode] = useState(false);

  const $updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if (!$isRangeSelection(selection)) {
      return;
    }

    const anchorNode = selection.anchor.getNode();
    let element =
      anchorNode.getKey() === "root"
        ? anchorNode
        : $findMatchingParent(anchorNode, (e) => {
            const parent = e.getParent();
            return parent !== null && $isRootOrShadowRoot(parent);
          });

    if (element === null) {
      element = anchorNode.getTopLevelElementOrThrow();
    }

    const elementKey = element.getKey();
    const elementDOM = activeEditor.getElementByKey(elementKey);

    // Update formatting
    setIsBold(selection.hasFormat("bold"));
    setIsItalic(selection.hasFormat("italic"));
    setIsUnderline(selection.hasFormat("underline"));
    setIsStrikethrough(selection.hasFormat("strikethrough"));

    if (elementDOM !== null) {
      // setSelectedElementKey(elementKey);
      if ($isListNode(element)) {
        const parentList = $getNearestNodeOfType<ListNode>(anchorNode, ListNode);
        const type = parentList ? parentList.getListType() : element.getListType();
        setBlockType(type);
      } else {
        const type = $isHeadingNode(element) ? element.getTag() : element.getType();
        if (type in blockTypeToBlockName) {
          setBlockType(type as keyof typeof blockTypeToBlockName);
        }
      }
    }
  }, [activeEditor]);

  useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      (_payload, newEditor) => {
        $updateToolbar();
        setActiveEditor(newEditor);
        return false;
      },
      COMMAND_PRIORITY_CRITICAL,
    );
  }, [editor, $updateToolbar]);

  useEffect(() => {
    return mergeRegister(
      editor.registerEditableListener((editable) => {
        setIsEditable(editable);
      }),
      activeEditor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          $updateToolbar();
        });
      }),
    );
  }, [$updateToolbar, activeEditor, editor]);

  const formatParagraph = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createParagraphNode());
      }
    });
  };

  const formatBulletList = () => {
    if (blockType !== "bullet") {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
      return;
    }
    editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
  };

  const formatNumberedList = () => {
    if (blockType !== "number") {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
      return;
    }
    editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
  };

  const formatQuote = () => {
    if (blockType !== "quote") {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, () => $createQuoteNode());
        }
      });
    }
  };

  const insertLink = useCallback(() => {
    if (!isLink) {
      setIsLinkEditMode(true);
      activeEditor.dispatchCommand(TOGGLE_LINK_COMMAND, sanitizeUrl("https://"));
    } else {
      setIsLinkEditMode(false);
      activeEditor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    }
  }, [activeEditor, setIsLinkEditMode, isLink]);

  return (
    <div className="space-x-2">
      <ToggleTextStyleButton editor={activeEditor} isStyleActive={isBold} isEditable={isEditable} formatType="bold">
        <BoldIcon className="h-4 w-4" strokeWidth={3} />
      </ToggleTextStyleButton>
      <ToggleTextStyleButton editor={activeEditor} isStyleActive={isItalic} isEditable={isEditable} formatType="italic">
        <ItalicIcon className="h-4 w-4" strokeWidth={3} />
      </ToggleTextStyleButton>
      <ToggleTextStyleButton
        editor={activeEditor}
        isStyleActive={isUnderline}
        isEditable={isEditable}
        formatType="underline"
      >
        <UnderlineIcon className="h-4 w-4" strokeWidth={3} />
      </ToggleTextStyleButton>
      <ToggleTextStyleButton
        editor={activeEditor}
        isStyleActive={isStrikethrough}
        isEditable={isEditable}
        formatType="strikethrough"
      >
        <StrikethroughIcon className="h-4 w-4" strokeWidth={3} />
      </ToggleTextStyleButton>
      <ToggleBlockTypeButton isEditable={isEditable} onClick={formatQuote} isBlockTypeActive={blockType === "quote"}>
        <QuoteIcon className="h-4 w-4" strokeWidth={2} />
      </ToggleBlockTypeButton>
      <ToggleBlockTypeButton isEditable={isEditable} onClick={insertLink} isBlockTypeActive={blockType === "link"}>
        <Link2Icon className="h-4 w-4" strokeWidth={2} />
      </ToggleBlockTypeButton>
      <ToggleBlockTypeButton
        isEditable={isEditable}
        onClick={formatBulletList}
        isBlockTypeActive={blockType === "bullet"}
      >
        <ListIcon className="h-4 w-4" strokeWidth={2} />
      </ToggleBlockTypeButton>
      <ToggleBlockTypeButton
        isEditable={isEditable}
        onClick={formatNumberedList}
        isBlockTypeActive={blockType === "number"}
      >
        <ListOrderedIcon className="h-4 w-4" strokeWidth={2} />
      </ToggleBlockTypeButton>
      <ToggleBlockTypeButton isEditable={isEditable} onClick={formatParagraph} isBlockTypeActive={false}>
        <AlignJustifyIcon className="h-4 w-4" strokeWidth={2} />
      </ToggleBlockTypeButton>
      <TriggerCommandButton
        onClick={() => {
          editor.dispatchCommand(INSERT_HORIZONTAL_RULE_COMMAND, undefined);
        }}
      >
        <MinusIcon className="h-4 w-4" strokeWidth={2} />
      </TriggerCommandButton>
    </div>
  );
}

interface ITriggerCommandButtonProps {
  onClick: () => void;
  children: ReactNode;
}

function TriggerCommandButton({ onClick, children }: ITriggerCommandButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={classNames(
        "rounded-lg p-2 hover:border-slate-200",
        "text-slate-500 hover:bg-slate-100 hover:text-slate-800",
        "outline-none focus:bg-slate-100 focus:text-slate-800 focus:ring-2 focus:ring-slate-300 focus:ring-offset-2",
      )}
    >
      {children}
    </button>
  );
}

interface IToggleBlockTypeButtonProps {
  isBlockTypeActive: boolean;
  isEditable: boolean;
  onClick: () => void;
  children: ReactNode;
}

function ToggleBlockTypeButton({ isBlockTypeActive, isEditable, onClick, children }: IToggleBlockTypeButtonProps) {
  return (
    <button
      type="button"
      disabled={!isEditable}
      onClick={onClick}
      className={classNames(
        "rounded-lg p-2 hover:border-slate-200",
        "text-slate-500 hover:bg-slate-100 hover:text-slate-800",
        "outline-none focus:bg-slate-100 focus:text-slate-800 focus:ring-2 focus:ring-slate-300 focus:ring-offset-2",
        {
          "bg-slate-100 text-slate-800": isBlockTypeActive,
        },
      )}
    >
      {children}
    </button>
  );
}

interface IToggleTextStyleButtonProps {
  editor: LexicalEditor;
  isStyleActive: boolean;
  isEditable: boolean;
  formatType: TextFormatType;
  children: ReactNode;
}

function ToggleTextStyleButton({
  editor,
  isStyleActive,
  isEditable,
  formatType,
  children,
}: IToggleTextStyleButtonProps) {
  return (
    <button
      type="button"
      disabled={!isEditable}
      onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, formatType)}
      className={classNames(
        "rounded-lg p-2 hover:border-slate-200",
        "text-slate-500 hover:bg-slate-100 hover:text-slate-800",
        "outline-none focus:bg-slate-100 focus:text-slate-800 focus:ring-2 focus:ring-slate-300 focus:ring-offset-2",
        {
          "bg-slate-100 text-slate-800": isStyleActive,
        },
      )}
    >
      {children}
    </button>
  );
}
